/** Partner page */
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import classnames from 'classnames'
import { GetStaticProps } from 'next'
import React from 'react'

import ButtonLink from '@aletheia/common/components/ButtonLink'
import { useBackgroundImage } from '@aletheia/common/components/ContentfulImage/background'
import Link from '@aletheia/common/components/Link'
import PageComponent from '@aletheia/common/components/Page'
import PrettyDate from '@aletheia/common/components/__legacy/utilities/PrettyDate'

import { fetchPosts, IPost, isEntry } from '../../contentful'

const usePostCardStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
    '& a': {
      color: theme.palette.text.primary,
    },
  },
  bannerImage: {
    backgroundColor: theme.palette.grey[300],
    height: 160,
    marginBottom: theme.spacing(3),
  },
}))

type PostCardProps = {
  Post: IPost
}

const PostCard: React.FC<PostCardProps> = ({ Post }) => {
  const classes = usePostCardStyles()
  const { title, authors, bannerImage, datePublished, slug } = Post.fields
  const backgroundImage = useBackgroundImage(bannerImage, { scale: 40 })
  const as = `/blog/${slug}`
  const href = `/blog/[slug]`
  return (
    <Card className={classes.root}>
      <CardContent>
        <Link href={href} as={as}>
          <div className={classnames(classes.bannerImage, backgroundImage)} />
        </Link>
        <Link href={href} as={as}>
          <Typography variant="h5" component="h3" gutterBottom>
            {title}
          </Typography>
        </Link>
        <Typography variant="body2">
          {authors.filter(isEntry).map((author, index) => (
            <React.Fragment key={author.sys.id}>
              {index > 0 &&
                (index === authors.length - 1 ? <> &amp; </> : <>, </>)}
              <Link href={`/people/${author.fields.slug}`}>
                {author.fields.title}
              </Link>
            </React.Fragment>
          ))}
          <> • </>
          <PrettyDate value={datePublished} />
        </Typography>
      </CardContent>
      <CardActions>
        <ButtonLink href={href} as={as} fullWidth>
          Read more <ChevronRightIcon />
        </ButtonLink>
      </CardActions>
    </Card>
  )
}

type BlogPageProps = {
  Posts: IPost[]
}

const BlogPage: React.FC<BlogPageProps> = ({ Posts }) => {
  return (
    <PageComponent title={'Blog'} pageTitle={`Blog`} variant="narrow">
      <Grid container justifyContent="center">
        {Posts.map((Post) => (
          <PostCard key={Post.sys.id} Post={Post} />
        ))}
      </Grid>
    </PageComponent>
  )
}

export default BlogPage

export const getStaticProps: GetStaticProps<BlogPageProps> = async (_ctx) => {
  const Posts = await fetchPosts()
  if (!Posts) throw new Error('Missing Posts')
  return {
    props: { Posts },
    revalidate: 1 * 60,
  }
}
